import React, { useState, useEffect, useRef } from 'react';
import portfolioAPI from '../services/portfolioAPI';
import './Skills.css';

const Skills = () => {
  const [skillsData, setSkillsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spotlight, setSpotlight] = useState({ active: false, cards: [] });
  const cardRefs = useRef([]);
  const currentSpotlightRef = useRef({ active: false, cards: [] });
  const targetSpotlightRef = useRef({ active: false, cards: [] });
  const animationFrameRef = useRef(null);
  const pointerStateRef = useRef({ x: 0, y: 0, t: 0 });
  const spotlightMotionRef = useRef(0.2);

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const animateSpotlight = () => {
    const current = currentSpotlightRef.current;
    const target = targetSpotlightRef.current;
    const total = Math.max(current.cards.length, target.cards.length);

    const nextCards = Array.from({ length: total }, (_, index) => {
      const currentCard = current.cards[index] || { x: 50, y: 50, intensity: 0 };
      const targetCard = target.cards[index] || { x: currentCard.x, y: currentCard.y, intensity: 0 };

      const positionEase = clamp(0.16 + spotlightMotionRef.current * 0.56, 0.16, 0.72);
      const intensityEase = clamp(0.18 + spotlightMotionRef.current * 0.34, 0.18, 0.52);

      return {
        x: currentCard.x + (targetCard.x - currentCard.x) * positionEase,
        y: currentCard.y + (targetCard.y - currentCard.y) * positionEase,
        intensity: currentCard.intensity + (targetCard.intensity - currentCard.intensity) * intensityEase,
      };
    });

    const hasVisibleIntensity = nextCards.some((card) => card.intensity > 0.012);
    const nextState = {
      active: target.active || hasVisibleIntensity,
      cards: hasVisibleIntensity || target.active ? nextCards : [],
    };

    currentSpotlightRef.current = nextState;
    setSpotlight(nextState);

    if (target.active || hasVisibleIntensity) {
      animationFrameRef.current = requestAnimationFrame(animateSpotlight);
    } else {
      animationFrameRef.current = null;
    }
  };

  const ensureSpotlightAnimation = () => {
    if (!animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(animateSpotlight);
    }
  };

  const normalizeSkills = (skills) => {
    if (!Array.isArray(skills)) {
      return [];
    }

    if (skills.length > 0 && Array.isArray(skills[0]?.skills)) {
      return skills.flatMap((group) => group.skills || []);
    }

    return skills;
  };

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const skills = await portfolioAPI.getSkills();

        const normalizedSkills = normalizeSkills(skills).map((skill) => ({
          name: skill.name,
          icon: skill.icon,
          category: skill.category,
        }));

        setSkillsData(normalizedSkills);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching skills:', error);
        setLoading(false);
      }
    };

    fetchSkills();
  }, []);

  useEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, skillsData.length);
  }, [skillsData]);

  useEffect(() => () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  const handleGridMouseMove = (event) => {
    const influenceRadius = 240;
    const now = performance.now();

    if (pointerStateRef.current.t > 0) {
      const elapsed = Math.max(1, now - pointerStateRef.current.t);
      const distance = Math.hypot(event.clientX - pointerStateRef.current.x, event.clientY - pointerStateRef.current.y);
      const pixelsPerMs = distance / elapsed;
      const normalizedSpeed = clamp(pixelsPerMs / 2.2, 0, 1);

      spotlightMotionRef.current = spotlightMotionRef.current * 0.65 + normalizedSpeed * 0.35;
    }

    pointerStateRef.current = { x: event.clientX, y: event.clientY, t: now };

    const cards = cardRefs.current.map((card) => {
      if (!card) {
        return { x: 50, y: 50, intensity: 0 };
      }

      const rect = card.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const dx = event.clientX - centerX;
      const dy = event.clientY - centerY;
      const distance = Math.hypot(dx, dy);
      const intensity = clamp(1 - distance / influenceRadius, 0, 1);

      const localX = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      const localY = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);

      return { x: localX, y: localY, intensity };
    });

    targetSpotlightRef.current = { active: true, cards };
    ensureSpotlightAnimation();
  };

  const handleGridMouseLeave = () => {
    pointerStateRef.current = { x: 0, y: 0, t: 0 };
    spotlightMotionRef.current = 0.2;

    const fadeCards = currentSpotlightRef.current.cards.map((card) => ({
      x: card.x,
      y: card.y,
      intensity: 0,
    }));

    targetSpotlightRef.current = { active: false, cards: fadeCards };
    ensureSpotlightAnimation();
  };

  if (loading) {
    return (
      <section id="skills" className="skills-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Skills</h2>
            <p className="skills-subtitle">These are the technologies I've worked with.</p>
            <div className="title-underline"></div>
          </div>
          <div className="skills-loading">Loading skills...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="skills" className="skills-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Skills</h2>
          <p className="skills-subtitle">These are the technologies I've worked with.</p>
          <div className="title-underline"></div>
        </div>
        <div className="skills-grid" onMouseMove={handleGridMouseMove} onMouseLeave={handleGridMouseLeave}>
          {skillsData.map((skill, index) => {
            const spot = spotlight.cards[index] || { x: 50, y: 50, intensity: 0 };

            return (
              <article
                key={`${skill.name}-${index}`}
                ref={(element) => {
                  cardRefs.current[index] = element;
                }}
                className={`skill-card skill-variant-${index % 6} ${spotlight.active && spot.intensity > 0.05 ? 'is-spotlight' : ''}`}
                style={{
                  '--spotlight-x': `${spot.x}%`,
                  '--spotlight-y': `${spot.y}%`,
                  '--proximity': spot.intensity.toFixed(3),
                }}
              >
                <h3 className="skill-name">{skill.name}</h3>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;